import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Sparkles, Loader2, Trash2, Plus } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { ErrorAlert } from '@/components/shared/ErrorAlert';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { TaskCard } from '@/features/tasks/components/TaskCard';

const STATUS_CONFIG: Record<string, { label: string; variant: 'default' | 'success' | 'warning' | 'secondary' }> = {
  active: { label: 'Active', variant: 'default' },
  completed: { label: 'Completed', variant: 'success' },
  on_hold: { label: 'On Hold', variant: 'warning' },
  archived: { label: 'Archived', variant: 'secondary' },
};

interface ProjectDetail {
  id: string;
  name: string;
  description: string;
  status: string;
  start_date: string | null;
  target_date: string | null;
  created_at: string;
}

interface ProjectTask {
  id: string;
  title: string;
  status: 'todo' | 'in_progress' | 'done' | 'cancelled';
  priority: 'low' | 'medium' | 'high' | 'critical';
  due_date: string | null;
}

export function ProjectDetailPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [aiInsight, setAiInsight] = useState<string | null>(null);

  const { data: project, isLoading, error, refetch } = useQuery({
    queryKey: ['project', projectId],
    queryFn: async () => {
      const { data, error: fetchError } = await supabase
        .from('project')
        .select('*')
        .eq('id', projectId!)
        .single();
      if (fetchError) throw fetchError;
      return data as unknown as ProjectDetail;
    },
    enabled: !!projectId,
  });

  const { data: tasks } = useQuery({
    queryKey: ['project-tasks', projectId],
    queryFn: async () => {
      const { data } = await supabase
        .from('task')
        .select('id, title, status, priority, due_date')
        .eq('project_id', projectId!)
        .is('deleted_at', null)
        .order('created_at', { ascending: false });
      return (data ?? []) as ProjectTask[];
    },
    enabled: !!projectId,
  });

  const deleteProject = useMutation({
    mutationFn: async () => {
      const { error: delError } = await supabase
        .from('project')
        .update({ deleted_at: new Date().toISOString() } as never)
        .eq('id', projectId!);
      if (delError) throw delError;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      navigate('/projects');
    },
  });

  const getAiInsight = useMutation({
    mutationFn: async () => {
      const { data, error: aiError } = await supabase.functions.invoke('project-ai', {
        body: { action: 'insight', project, tasks },
      });
      if (aiError) throw aiError;
      return data.result as string;
    },
    onSuccess: (result) => setAiInsight(result),
  });

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (error || !project) {
    return <ErrorAlert message={error ? String(error) : 'Project not found'} onRetry={refetch} />;
  }

  const statusConfig = STATUS_CONFIG[project.status] ?? { label: project.status, variant: 'default' as const };
  const completedCount = (tasks ?? []).filter((t) => t.status === 'done').length;
  const totalCount = (tasks ?? []).length;

  return (
    <div>
      <div className="mb-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/projects')}>
          <ArrowLeft className="mr-2 h-4 w-4" /> Back to Projects
        </Button>
      </div>

      <PageHeader
        title={project.name}
        action={
          <div className="flex gap-2">
            <Button variant="destructive" size="icon" onClick={() => deleteProject.mutate()}>
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        }
      />

      <div className="grid gap-6 md:grid-cols-3">
        <div className="md:col-span-2 space-y-4">
          <Card>
            <CardHeader><CardTitle className="text-base">Description</CardTitle></CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                {project.description || 'No description provided.'}
              </p>
            </CardContent>
          </Card>

          {/* Tasks */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-base">
                Tasks ({completedCount}/{totalCount})
              </CardTitle>
              <Button variant="outline" size="sm" onClick={() => navigate('/tasks')}>
                <Plus className="mr-2 h-4 w-4" /> Add Task
              </Button>
            </CardHeader>
            <CardContent className="space-y-2">
              {(tasks ?? []).length === 0 ? (
                <p className="text-sm text-muted-foreground">No tasks yet.</p>
              ) : (
                (tasks ?? []).map((t) => (
                  <TaskCard
                    key={t.id}
                    task={t}
                    onClick={() => navigate(`/tasks/${t.id}`)}
                  />
                ))
              )}
            </CardContent>
          </Card>

          {/* AI Insight */}
          <Card>
            <CardContent className="pt-6">
              <Button
                variant="outline"
                size="sm"
                onClick={() => getAiInsight.mutate()}
                disabled={getAiInsight.isPending}
              >
                {getAiInsight.isPending
                  ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  : <Sparkles className="mr-2 h-4 w-4" />}
                AI Project Insight
              </Button>
              {aiInsight && (
                <p className="mt-3 text-sm whitespace-pre-wrap">{aiInsight}</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          <Card>
            <CardHeader><CardTitle className="text-base">Details</CardTitle></CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Status</span>
                <Badge variant={statusConfig.variant}>{statusConfig.label}</Badge>
              </div>
              {project.start_date && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Start Date</span>
                  <span>{new Date(project.start_date).toLocaleDateString()}</span>
                </div>
              )}
              {project.target_date && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Target Date</span>
                  <span>{new Date(project.target_date).toLocaleDateString()}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-muted-foreground">Created</span>
                <span>{new Date(project.created_at).toLocaleDateString()}</span>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
